import { useTranslation } from "react-i18next";
import { cn } from "@cieda/djayma-ui";
import {
  MapPin,
  Phone,
  Clock,
  Package,
  User,
  Truck,
} from "lucide-react";
import type { Delivery } from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DeliveryCardProps {
  readonly delivery: Delivery;
  readonly onAssign?: (delivery: Delivery) => void;
  readonly isDragging?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return new Intl.NumberFormat("fr-SN", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat("fr-SN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
}

const PAYMENT_LABELS: Readonly<Record<string, string>> = {
  cod: "Paiement à la livraison",
  wave: "Wave",
  orange_money: "Orange Money",
  card: "Carte bancaire",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DeliveryCard({ delivery, onAssign, isDragging = false }: DeliveryCardProps) {
  const { t } = useTranslation("admin");
  const { order, driver, zone } = delivery;

  return (
    <div
      className={cn(
        "rounded-lg border border-stone-200 bg-white p-3.5 transition-shadow",
        "hover:shadow-md cursor-grab active:cursor-grabbing",
        isDragging && "shadow-lg ring-2 ring-amber-300 opacity-90",
      )}
    >
      {/* Header row: reference + amount */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-stone-900">
            {order.public_ref}
          </p>
          <p className="mt-0.5 truncate text-xs text-stone-500">
            {order.customer_name}
          </p>
        </div>
        <span className="shrink-0 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
          {formatFcfa(order.total_ttc)} FCFA
        </span>
      </div>

      {/* Details */}
      <div className="mt-3 space-y-1.5">
        {/* Address */}
        <div className="flex items-start gap-2 text-xs text-stone-600">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
          <span className="line-clamp-2">
            {delivery.address ?? order.delivery_address}
          </span>
        </div>

        {/* Phone */}
        <div className="flex items-center gap-2 text-xs text-stone-600">
          <Phone className="h-3.5 w-3.5 shrink-0 text-stone-400" />
          <span>{order.customer_phone}</span>
        </div>

        {/* Payment method */}
        <div className="flex items-center gap-2 text-xs text-stone-600">
          <Package className="h-3.5 w-3.5 shrink-0 text-stone-400" />
          <span>{PAYMENT_LABELS[order.payment_method] ?? order.payment_method}</span>
        </div>

        {/* Created date */}
        <div className="flex items-center gap-2 text-xs text-stone-500">
          <Clock className="h-3.5 w-3.5 shrink-0 text-stone-400" />
          <span>{formatDate(order.created_at)}</span>
        </div>
      </div>

      {/* Footer: zone + driver info */}
      <div className="mt-3 flex items-center justify-between border-t border-stone-100 pt-2.5">
        {/* Zone badge */}
        {zone && (
          <span className="inline-flex items-center rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-medium text-stone-600">
            {zone.name}
          </span>
        )}

        {/* Driver or assign button */}
        {driver ? (
          <div className="flex items-center gap-1.5 text-xs text-stone-600">
            <Truck className="h-3.5 w-3.5 text-emerald-600" />
            <span className="font-medium">{driver.full_name}</span>
          </div>
        ) : (
          onAssign && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onAssign(delivery);
              }}
              className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800 transition-colors hover:bg-amber-200"
            >
              <User className="h-3 w-3" />
              {t("delivery_board.assign")}
            </button>
          )
        )}
      </div>

      {/* Estimated delivery time */}
      {delivery.estimated_at && (
        <div className="mt-2 flex items-center gap-1.5 text-[10px] text-stone-400">
          <Clock className="h-3 w-3" />
          <span>
            {t("delivery_board.estimated")}: {formatDate(delivery.estimated_at)}
          </span>
        </div>
      )}
    </div>
  );
}
